/**
 * Tez eslatma: qo'ng'iroq paytida yozib qo'yish. Matn widget sozlamasida (serverda, `version` bilan) -- har qurilmada va
 * boshqa oynalarda real-time. Yozilgach 600 ms kutib saqlanadi; yozib turganda boshqa oynadan kelgan matn ustidan yozmaydi.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StickyNote } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { currentLayout, saveLayout } from "../api";
import { configureItem } from "../layout";
import { NOTE_MAX, type NoteConfig } from "./index";

type Props = { item: WidgetItem<NoteConfig>; me: Me };

export function NoteView({ item }: Props) {
  const { t } = useTranslation("widgets");
  const [text, setText] = useState(item.config.text);
  const ref = useRef<HTMLTextAreaElement>(null);
  const timer = useRef<number | null>(null);
  const serverText = item.config.text;

  useEffect(() => {
    // Boshqa oyna yangilagan bo'lsa -- faqat bu yerda yozilmayotgan paytda olinadi
    if (document.activeElement !== ref.current) setText(serverText);
  }, [serverText]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  function change(next: string) {
    setText(next);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const cur = currentLayout();
      if (cur && cur.items.some((i) => i.id === item.id)) saveLayout(configureItem(cur, item.id, { text: next }));
    }, 600);
  }

  return (
    <div className="relative h-full">
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => change(e.target.value.slice(0, NOTE_MAX))}
        placeholder={t("note.placeholder")}
        maxLength={NOTE_MAX}
        spellCheck={false}
        aria-label={t("note.title")}
        className={cn(
          "h-full w-full resize-none bg-transparent px-3 py-2.5 text-[12px] leading-snug text-ink outline-none placeholder:text-muted",
          item.size === "s" && "text-[11px]",
        )}
      />
      {text.length > NOTE_MAX - 100 && <span className="absolute right-2 bottom-1 text-[10px] text-muted tabular-nums">{NOTE_MAX - text.length}</span>}
    </div>
  );
}

export function NoteGlance({ item }: Props) {
  const { t } = useTranslation("widgets");
  const line = item.config.text.split("\n").find((l) => l.trim()) ?? "";
  return (
    <span className="inline-flex max-w-[180px] items-center gap-1.5" title={line || t("note.title")}>
      <StickyNote size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("truncate text-[12px]", line ? "font-medium" : "text-muted")}>{line || t("note.empty")}</span>
    </span>
  );
}
